import { MaterialCommunityIcons } from "@expo/vector-icons";
import { View, Text, StyleSheet, Pressable, Linking, Platform } from "react-native";

import { TravelMap } from "@/components/maps/TravelMap";
import type { FormDefinition } from "@/lib/api";
import { colors, fonts, spacing, radius, shadows } from "@/lib/theme";

interface AddressSectionProps {
    patient: NonNullable<FormDefinition["patient"]>;
    visitId: string;
}

export function AddressSection({ patient, visitId }: AddressSectionProps) {
    const hasLocation = patient.lat != null && patient.lng != null;

    const handleViewDirections = () => {
        if (!hasLocation) {
            return;
        }
        // Use universal https URL — iOS will offer installed map apps, Android opens default
        const url = `https://www.google.com/maps/dir/?api=1&destination=${patient.lat},${patient.lng}`;
        Linking.openURL(url);
    };

    return (
        <View style={styles.card}>
            <Text style={styles.sectionLabel}>LOCATION</Text>

            {/* Address — above the map */}
            {patient.address ? <Text style={styles.addressLine}>{patient.address}</Text> : null}
            {patient.addressLine2 ? (
                <Text style={styles.addressLine}>{patient.addressLine2}</Text>
            ) : null}

            {/* Map + Transport buttons */}
            <View style={styles.mapWrapper}>
                <TravelMap
                    patientLat={patient.lat}
                    patientLng={patient.lng}
                    patientName={`${patient.firstName} ${patient.lastName}`}
                    visitId={visitId}
                    height={180}
                />
            </View>

            {/* Address instructions — below transport buttons */}
            {patient.addressInstructions ? (
                <View style={styles.instructionsBanner}>
                    <Text style={styles.instructionsBody}>
                        <Text style={styles.instructionsLabel}>Instructions: </Text>
                        {patient.addressInstructions}
                    </Text>
                </View>
            ) : null}

            {/* View Directions button */}
            {hasLocation ? (
                <Pressable
                    onPress={handleViewDirections}
                    style={({ pressed }) => [
                        styles.directionsButton,
                        pressed && styles.directionsPressed
                    ]}
                >
                    <MaterialCommunityIcons name="directions" size={18} color={colors.teal600} />
                    <Text style={styles.directionsText}>View Directions</Text>
                </Pressable>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.base,
        ...shadows.sm
    },
    sectionLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.teal500,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: spacing.sm
    },
    addressLine: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.textPrimary,
        lineHeight: 20
    },
    mapWrapper: {
        marginTop: spacing.md
    },
    instructionsBanner: {
        backgroundColor: colors.warning50,
        borderWidth: 1,
        borderColor: colors.warning400,
        borderRadius: radius.md,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        marginTop: spacing.md
    },
    instructionsBody: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textPrimary,
        lineHeight: 18
    },
    instructionsLabel: {
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    directionsButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        borderWidth: 1.5,
        borderColor: colors.teal400,
        borderRadius: radius.full,
        paddingVertical: spacing.md,
        marginTop: spacing.md
    },
    directionsPressed: {
        backgroundColor: colors.teal50
    },
    directionsText: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.teal600
    }
});
